import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { EditConfigDialog } from "../components/EditConfigDialog";
import { useConfigsQuery } from "../hooks/useConfigsQuery";
import { useUpdateConfigMutation } from "../hooks/useUpdateConfigMutation";
import type { ConfigItem } from "../types";

export const ConfigsPage: React.FC = () => {
	const { data: configs = [], isPending } = useConfigsQuery();
	const { mutateAsync: updateConfig, isPending: isSaving } =
		useUpdateConfigMutation();
	const [selectedConfig, setSelectedConfig] = React.useState<ConfigItem | null>(
		null,
	);

	const handleEdit = React.useCallback((config: ConfigItem) => {
		setSelectedConfig(config);
	}, []);

	const handleCloseDialog = React.useCallback(() => {
		setSelectedConfig(null);
	}, []);

	const handleSubmit = React.useCallback(
		async (value: string) => {
			if (!selectedConfig) {
				return;
			}

			try {
				await updateConfig({
					id: selectedConfig.id,
					value,
					key: selectedConfig.key,
				});
				toast.success("Configuração atualizada.");
				setSelectedConfig(null);
			} catch (error) {
				toast.error(
					"Não foi possível atualizar a configuração. Tente novamente.",
				);
				throw error;
			}
		},
		[selectedConfig, updateConfig],
	);

	const renderSkeleton = () => (
		<TableBody>
			{Array.from({ length: 5 }).map((_, index) => (
				<TableRow key={`config-skeleton-${index}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-36 rounded bg-muted" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-48 rounded bg-muted/70" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-20 rounded bg-muted/50" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderRows = () => (
		<TableBody>
			{configs.map((config) => (
				<TableRow key={config.id}>
					<TableCell className="font-medium text-foreground">
						{config.key}
					</TableCell>
					<TableCell className="text-muted-foreground">
						{config.value}
					</TableCell>
					<TableCell className="flex justify-end">
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => handleEdit(config)}
						>
							Editar
						</Button>
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={3}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Não existem configurações disponíveis.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<div className="space-y-6 p-4">
			<div>
				<h1 className="text-lg font-semibold leading-6">Configurações</h1>
				<p className="text-sm text-muted-foreground">
					Consulte e atualize as configurações globais da aplicação.
				</p>
			</div>

			<div className="overflow-hidden rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-1/3">Chave</TableHead>
							<TableHead>Valor</TableHead>
							<TableHead className="w-32 text-right">Ações</TableHead>
						</TableRow>
					</TableHeader>

					{isPending
						? renderSkeleton()
						: configs.length > 0
							? renderRows()
							: renderEmpty()}
				</Table>
			</div>

			{selectedConfig ? (
				<EditConfigDialog
					config={selectedConfig}
					isSaving={isSaving}
					onSubmit={handleSubmit}
					onCancel={handleCloseDialog}
				/>
			) : null}
		</div>
	);
};
